import React, { useEffect, useState } from 'react';

import { Form, Input, Layout, Select, message, Row, Col } from 'antd';
import { get } from 'lodash';

import CategoryService from 'services/category';
import PdButton from 'components/pd-button';
import PdUploader from 'components/pd-uploader';
import { PageSpinner } from 'components/page-spinner';

import { errHandling } from 'utils';

import './style.scss';
import { useNavigate, useLocation } from 'react-router-dom';

const { Content } = Layout;
const { Option } = Select;

const FIELDS = {
  CATEGORIES_ATTRIBUTES: 'parent_id',
  ICON: 'icon',
  TITLE: 'title',
  TITLE_BAHASA: 'title_idn',
};

export function CategoryCreateEdit() {
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const [value, setValue] = useState({});
  const [loadingButton, setLoadingButton] = useState(false);
  const [selectedParent, setSelectedParent] = useState('');
  const [parentCategoryOption, setParentCategoryOption] = useState([]);
  const [parentId, setParentId] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categoryOption, setCategoryOption] = useState([]);
  const [formLayout] = useState('vertical');
  const [loading, setLoading] = useState(false);

  const urlParams = new URLSearchParams(location.search);
  const id = urlParams.get('id');

  const formItemLayout =
    formLayout === 'horizontal'
      ? {
          labelCol: { span: 4 },
          wrapperCol: { span: 14 },
        }
      : null;

  const buttonItemLayout =
    formLayout === 'horizontal'
      ? {
          wrapperCol: { span: 14, offset: 4 },
        }
      : null;

  async function getCategory(id) {
    const { data } = await CategoryService.showCategory(id);
    const categoryData = data.data;
    return categoryData;
  }

  async function setCategoryState(id) {
    try {
      setLoading(true);
      const currentCategory = await getCategory(id);
      setValue(currentCategory);
      if (get(currentCategory, 'parent_id', null) !== null) {
        const firstParent = await getCategory(
          get(currentCategory, 'parent_id', '')
        );
        if (get(firstParent, 'parent_id', null) !== null) {
          setSelectedCategory(firstParent.title);
          const Parent = await getCategory(get(firstParent, 'parent_id', ''));
          setSelectedParent(get(Parent, 'title', ''));
          setParentId((prevState) => ({
            ...prevState,
            0: Parent.id,
          }));
        } else {
          setSelectedParent(get(firstParent, 'title', ''));
          setParentId((prevState) => ({
            ...prevState,
            0: firstParent.id,
          }));
        }
      }
    } finally {
      setLoading(false);
    }
  }

  /* eslint-disable */
  useEffect(() => {
    if (id) {
      setCategoryState(id);
    } else {
      setValue([]);
    }
  }, [location, navigate, id]);
  /* eslint-enable */

  async function getCategories() {
    try {
      const { data } = await CategoryService.getActiveCategoryList();
      setParentCategoryOption(data.data);
    } catch (err) {
      errHandling(err);
    }

    for (let [key, value] of Object.entries(parentId)) {
      try {
        const { data } = await CategoryService.getActiveCategoryList(value);
        if (key === '0') {
          setCategoryOption(data.data);
        }
      } catch (err) {
        errHandling(err);
      }
    }
  }

  useEffect(() => {
    getCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentId]);

  useEffect(() => {
    if (id && value.title) {
      form.setFieldsValue({
        [FIELDS.TITLE]: value[FIELDS.TITLE],
        [FIELDS.TITLE_BAHASA]: value[FIELDS.TITLE_BAHASA],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, id]);

  async function onFinish(values) {
    try {
      delete value['children'];
      setLoadingButton(true);
      if (id) {
        await CategoryService.updateCategory([{ ...value, ...values }]);
        message.success('Successfully updated category');
      } else {
        await CategoryService.createCategory({ ...value, ...values });
        message.success('Successfully created category');
      }
      navigate({
        pathname: '/categories',
      });
    } catch (err) {
      errHandling(err);
    } finally {
      setLoadingButton(false);
    }
  }

  function changeValue(e, key) {
    // e.persist();
    if (e) {
      setValue((prevState) => ({
        ...prevState,
        [key]: e,
      }));
    }
  }

  function onChange(val, key, index) {
    if (key === FIELDS.CATEGORIES_ATTRIBUTES) {
      let newValue = '';
      if (value[key]) {
        newValue = value[key];
      }
      let collection = [];
      if (index === 0) {
        collection = parentCategoryOption;
        setSelectedParent(val);
        setSelectedCategory('');
      } else if (index === 1) {
        collection = categoryOption;
        setSelectedCategory(val);
      }
      if (val) {
        const category = collection.find((x) => x.title === val);
        newValue = category.id;
        setValue((prevState) => ({
          ...prevState,
          [key]: newValue,
        }));
        setParentId((prevState) => ({
          ...prevState,
          [index]: category.id,
        }));
      } else {
        if (index === 0) {
          setValue((prevState) => ({
            ...prevState,
            [key]: null,
          }));
        } else if (index === 1) {
          collection = parentCategoryOption;
          const category = collection.find((x) => x.title === selectedParent);
          newValue = category.id;
          setValue((prevState) => ({
            ...prevState,
            [key]: newValue,
          }));
        }
      }
    }
  }

  if (loading) {
    return <PageSpinner />;
  }

  return (
    <Layout className="pd-cms-category main-container">
      <div className="container">
        <div className="content">
          <div className="pageTitle">{id ? 'Edit' : 'Create New'} Category</div>
          <Content>
            <Form
              layout={formLayout}
              form={form}
              onFinish={onFinish}
              initialValues={{
                [FIELDS.TITLE]: value[FIELDS.TITLE],
                [FIELDS.TITLE_BAHASA]: value[FIELDS.TITLE_BAHASA],
              }}>
              <div className="section-container">
                <div className="inner-section">
                  <Form.Item
                    label="Title"
                    name={FIELDS.TITLE}
                    {...formItemLayout}
                    rules={[
                      { required: true, message: 'Please input Title!' },
                    ]}>
                    <Input
                      placeholder="Write category title here"
                      onChange={(e) => changeValue(e, FIELDS.TITLE)}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Title (bahasa)"
                    name={FIELDS.TITLE_BAHASA}
                    {...formItemLayout}
                    rules={[
                      {
                        required: true,
                        message: 'Please input title bahasa!',
                      },
                    ]}>
                    <Input
                      placeholder="Write category title bahasa here"
                      onChange={(e) => changeValue(e, FIELDS.TITLE_BAHASA)}
                    />
                  </Form.Item>
                  <Row gutter={20}>
                    <Col span={12}>
                      {parentCategoryOption.length > 0 && (
                        <Form.Item label="Parent Category" {...formItemLayout}>
                          <Select
                            className="inputForm"
                            showSearch
                            style={{ width: 200 }}
                            placeholder="Select parent category"
                            value={selectedParent}
                            onChange={(val) =>
                              onChange(val, FIELDS.CATEGORIES_ATTRIBUTES, 0)
                            }>
                            <Option selected value="">
                              {'No Parent Category'}
                            </Option>
                            {parentCategoryOption.length > 0 &&
                              parentCategoryOption.map((category) => (
                                <Option
                                  value={category.title}
                                  key={category.id}>
                                  {category.title}
                                </Option>
                              ))}
                          </Select>
                        </Form.Item>
                      )}
                    </Col>
                    <Col span={12}>
                      {categoryOption.length > 0 && selectedParent !== '' && (
                        <Form.Item label="Category" {...formItemLayout}>
                          <Select
                            className="inputForm"
                            showSearch
                            style={{ width: 200 }}
                            placeholder="Select category"
                            value={selectedCategory}
                            onChange={(val) =>
                              onChange(val, FIELDS.CATEGORIES_ATTRIBUTES, 1)
                            }>
                            <Option selected value="">
                              {'No Category'}
                            </Option>
                            {categoryOption.length > 0 &&
                              categoryOption.map((category) => (
                                <Option
                                  value={category.title}
                                  key={category.id}>
                                  {category.title}
                                </Option>
                              ))}
                          </Select>
                        </Form.Item>
                      )}
                    </Col>
                  </Row>
                  <Form.Item label="Icon (70x70)" {...formItemLayout}>
                    <PdUploader
                      afterUpload={(val) => changeValue(val, FIELDS.ICON)}
                      imgUrl={value[FIELDS.ICON]}
                      keyValue={FIELDS.ICON}
                      name="Icon"
                    />
                  </Form.Item>
                </div>
              </div>

              <div className="section-container">
                <Form.Item {...buttonItemLayout}>
                  <div className="buttonSection mt-40">
                    <PdButton
                      htmlType="submit"
                      loading={loadingButton}
                      type="primary"
                      full>
                      Publish
                    </PdButton>
                  </div>
                </Form.Item>
              </div>
            </Form>
          </Content>
        </div>
      </div>
    </Layout>
  );
}

export default CategoryCreateEdit;
